define("shared/utils/export-to-pdf", ["exports", "html2pdf.js", "shared/modifiers/load-image-async"], function (_exports, _html2pdf, _loadImageAsync) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.PDF_PAGE_BREAK_CLASS = _exports.PDF_KEEP_TOGETHER_CLASS = _exports.PDF_EXPORTING_CLASS = void 0;
  _exports.exportToPdf = exportToPdf;
  _exports.registerPdfExportPrepare = registerPdfExportPrepare;
  _exports.runPdfExportPreparations = runPdfExportPreparations;
  _exports.waitForImages = waitForImages;
  0; //eaimeta@70e063a35619d71f0,"html2pdf.js",0,"shared/modifiers/load-image-async"eaimeta@70e063a35619d71f
  /**
   * Per-page overrides for the centralized PDF export. Pages customize the export
   * by passing this object directly to `exportToPdf`.
   */

  /**
   * Added to the export target for the duration of the capture. Pages/components
   * can hook into this class to render export-friendly variants of UI that does
   * not translate to a static document — e.g. expanding fixed-height scroll areas
   * or unclamping text that is truncated with an ellipsis on screen
   */
  const PDF_EXPORTING_CLASS = _exports.PDF_EXPORTING_CLASS = 'o--pdf-exporting';

  /**
   * Marks an element that must never be split across pages — it moves whole to the
   * next page instead. Apply it to charts, or to a header that should stay with
   * its content. Included in the default `avoid` list below.
   */
  const PDF_KEEP_TOGETHER_CLASS = _exports.PDF_KEEP_TOGETHER_CLASS = 'o--pdf-keep-together';

  /**
   * html2pdf's marker class for a manual page break. A page that places these
   * (`<div class="html2pdf__page-break"></div>`) is opting into explicit pagination:
   * `exportToPdf` detects them and flows content naturally instead of forcing
   * `avoid-all`, which would fight the breaks and leave large gaps. So a page never
   * has to remember to also override the pagebreak `mode`.
   */
  const PDF_PAGE_BREAK_CLASS = _exports.PDF_PAGE_BREAK_CLASS = 'html2pdf__page-break';
  const DEFAULT_PAGEBREAK_AVOID = [`.${PDF_KEEP_TOGETHER_CLASS}`];

  /**
   * Work that must run on the export target right before capture, and finish
   * before html2canvas reads the DOM — e.g. snapshotting a `<video>` frame into an
   * `<img>` the static document can actually rasterize. The hook should resolve
   * only once its DOM changes are rendered.
   */

  const prepareHooks = new Set();

  /**
   * Registers a {@link PdfExportPrepareHook} scoped to `element`. The hook runs
   * only when `element` is inside the target being exported, so a component can
   * defer expensive export-only work (capturing a frame, expanding a list) until
   * an export that actually includes it. Returns a disposer — call it on teardown.
   */
  function registerPdfExportPrepare(element, hook) {
    const entry = {
      element,
      hook
    };
    prepareHooks.add(entry);
    return () => {
      prepareHooks.delete(entry);
    };
  }

  /**
   * Runs every registered prepare hook whose element is inside `target`, in
   * parallel, and waits for all of them. A failing hook is isolated (settled, not
   * thrown) so one bad capture never aborts the export.
   */
  async function runPdfExportPreparations(target) {
    const pending = [...prepareHooks].filter(_ref => {
      let {
        element
      } = _ref;
      return target === element || target.contains(element);
    }).map(_ref2 => {
      let {
        hook
      } = _ref2;
      return Promise.resolve().then(hook);
    });
    await Promise.allSettled(pending);
  }

  // Upper bound on how long we wait for a single image to finish loading before
  // capturing anyway — a broken or hung image should never stall the export.
  const IMAGE_LOAD_TIMEOUT_MS = 5000;

  /**
   * Resolves once `img` is safe to capture: its real source has loaded (or a
   * timeout elapses). Images loaded through the `load-image-async` modifier show a
   * placeholder while their real source downloads in the background, so waiting on
   * `complete` alone would capture the placeholder — we also wait for the modifier
   * to clear its {@link ASYNC_IMAGE_LOADING_ATTR} marker.
   */
  function settleImage(img) {
    const isSettled = () => !img.hasAttribute(_loadImageAsync.ASYNC_IMAGE_LOADING_ATTR) && img.complete;
    return new Promise(resolve => {
      const start = performance.now();
      const check = () => {
        if (isSettled() || performance.now() - start >= IMAGE_LOAD_TIMEOUT_MS) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };

      // Runs synchronously first, so an already-settled image resolves without
      // scheduling a frame.
      check();
    });
  }

  /**
   * Waits for every `<img>` in `target` to finish loading its real source before
   * capture. Export flows that reveal previously-hidden content (e.g. expanding a
   * paginated table) render fresh images whose downloads are still in flight when
   * html2canvas would otherwise read the DOM, leaving them blank in the PDF.
   */
  async function waitForImages(target) {
    const images = [...target.querySelectorAll('img')];
    await Promise.all(images.map(img => settleImage(img)));
  }

  // CSS reference pixels are defined at 96 px per inch; jsPDF measures in mm.
  const PX_PER_INCH = 96;
  const MM_PER_INCH = 25.4;

  // Slack (mm) added to the derived page height so px→mm rounding doesn't spill a
  // sliver onto a second blank page. Imperceptible on multi-page reports.
  const PAGE_HEIGHT_SLACK_MM = 4;

  /**
   * Derives the default page size and orientation from the export target's own
   * dimensions so the exported PDF matches the content being captured, rather than
   * forcing a fixed A4 sheet or guessing from the viewport. We use `scrollWidth`/
   * `scrollHeight` so any content that overflows the visible box is still captured,
   * convert from CSS px to mm, and let the orientation follow the element's aspect
   * ratio. A page can still pin a specific size by passing `format`/`orientation`
   * overrides.
   *
   * Call this after `PDF_EXPORTING_CLASS` is applied so the measurement reflects
   * the export-friendly layout (expanded scroll areas, unclamped text, etc.).
   */
  function elementPageDefaults(element) {
    const toMm = px => px * MM_PER_INCH / PX_PER_INCH;
    const widthMm = toMm(element.scrollWidth);
    const heightMm = toMm(element.scrollHeight) + PAGE_HEIGHT_SLACK_MM;
    return {
      format: [widthMm, heightMm],
      orientation: widthMm >= heightMm ? 'landscape' : 'portrait'
    };
  }
  async function exportToPdf(element, filename) {
    let opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    // A page that placed pagination hints — manual breaks or keep-together markers
    // — wants explicit control, so flow content naturally rather than forcing
    // `avoid-all` (which would supersede the keep-together markers and fight the
    // breaks, leaving gaps). Otherwise keep `avoid-all` so simple pages never split
    // an element mid-page.
    const usesExplicitPagination = element.querySelector(`.${PDF_PAGE_BREAK_CLASS}, .${PDF_KEEP_TOGETHER_CLASS}`) !== null;
    const defaultPagebreak = {
      mode: usesExplicitPagination ? ['css', 'legacy'] : ['avoid-all', 'css', 'legacy'],
      avoid: DEFAULT_PAGEBREAK_AVOID
    };

    // Apply the export class before measuring so the page defaults reflect the
    // export-friendly layout (expanded scroll areas, unclamped text, etc.).
    element.classList.add(PDF_EXPORTING_CLASS);
    try {
      // Let components swap in export-friendly DOM (e.g. a captured video frame)
      // now that the export class is applied, before we measure or capture.
      await runPdfExportPreparations(element);

      // Wait for any images still downloading their real source (freshly rendered
      // rows, async-loaded avatars) so html2canvas doesn't capture them blank.
      await waitForImages(element);
      const pageDefaults = elementPageDefaults(element);
      const options = {
        margin: opts.margin ?? 10,
        filename,
        image: {
          type: 'jpeg',
          quality: 0.98,
          ...opts.image
        },
        // eslint-disable-next-line @latermedia/later-linting/avoid-numerals-in-name
        html2canvas: {
          scale: 2,
          useCORS: true,
          ...opts.html2canvas
        },
        jsPDF: {
          unit: 'mm',
          format: opts.format ?? pageDefaults.format,
          orientation: opts.orientation ?? pageDefaults.orientation
        },
        // Merge so page overrides (e.g. a custom `mode`) still inherit the default
        // `avoid` list unless they explicitly replace it.
        pagebreak: {
          ...defaultPagebreak,
          ...opts.pagebreak
        }
      };
      await (0, _html2pdf.default)().set(options).from(element).save();
    } finally {
      element.classList.remove(PDF_EXPORTING_CLASS);
    }
  }
});